// tslint:disable:object-literal-sort-keys

// Individual lodash imports to optimize bundle size
import get from 'lodash/get';
import range from 'lodash/range';
import * as React from 'react';

// Util
import { getProfileUrlById } from '../../config';
import { prettifyOunces } from '../../util/util';

// Types
import {
  IAnglerDayRedis,
  IAnglerRedis,
  ITournamentRedis,
  ILeaderboardAnglerDataApi,
  IDailyWeightDataApi,
} from '../../types';
import { ILeaderboardAnglerRedis, ILeaderboardPerDay } from './types';

/**
 * Process the raw response to shape resulting date.
 * and Cap results to limit, if there is one
 * @param data
 * @param limit
 */
export function processNewLeaderboardApi(
  data: ILeaderboardAnglerDataApi[],
  limit?: number
): ILeaderboardAnglerDataApi[] {
  return (limit ? data.slice(0, limit) : data);
}

/**
 * An angler's "DAY" has a common pattern for retrieval of information
 * @param day
 */
const getCountWeight = (day: IAnglerDayRedis) => ({
  count: parseInt(get(day, 'DAYFISHCOUNT', '0'), 10),
  weight: parseInt(get(day, 'DAYWEIGHT.@.DAYWEIGHT_OZ', '0'), 10),
});

/**
 * An angler's "DAY" has a common pattern for retrieval of information
 * @param day
 */
const getNewCountWeight = (day: IDailyWeightDataApi) => ({
  count: get(day, 'FishCount', '0'),
  weight: get(day, 'TotalOuncesAfterPenalty', '0'),
});

/**
 * Generate columns for Leaderboard from Data API data.
 * @param entry
 */
export function buildNewLeaderboardRow(
  entry: ILeaderboardAnglerDataApi,
) {
  // The actual recorded days
  const perDay: ILeaderboardPerDay[] = [];

  const prevDays = get(entry, 'DailyWeight');

  // "There is at least one prev day"
  if (prevDays.length) {
    // Ensure previous days (array or object) is array, transform, spread.
    perDay.push(...prevDays.map(getNewCountWeight));
  }

  // Current day will always be last (or ONLY if prevDays does not exist)
  perDay.push({
    count: parseInt(get(entry, 'todays_FishCount', '0'), 10),
    weight: parseInt(get(entry, 'todays_TotalOuncesAfterPenalty', '0'), 10),
  });

  // For each day in the tournament
  // TODO: calculate the number of days in the tournament.
  const allDays = range(1, perDay.length + 1).reduce(
    (col: any, day, tournamentIndex) => {
      // Default
      let cell = <span>-</span>;

      // Is there a perDay entry for the current tournament day?
      const dayTournament = perDay[tournamentIndex];
      if (dayTournament) {
        cell =
          dayTournament.count > 0 ? (
            <span>
              <span className="count">{dayTournament.count}</span>
              <span> / </span>
              <span className="weight">
                {prettifyOunces(dayTournament.weight, false)}
              </span>
            </span>
          ) : (
            <span>0</span>
          );
      }

      col[`Day ${day}<br/>Fish / Weight`] = cell;
      return col;
    },
    {}
  );

  const buildName = (
    name: string,
    id: number,
    secondName?: string,
    secondId?: number,
    teamName?: string,
  ) => {
    let name1 = name;
    let name2 = secondName ?? '';
    let team = teamName ?? '';

    if (secondId && !secondName && name.indexOf(' - ') !== -1) {
      const parts = name.split(' - ');
      name1 = parts[0];
      name2 = parts[1];
      team = parts[2] ?? '';
    }
    return (
      <div>
        <div className="leaderboard__name">
          <a href={getProfileUrlById(id)}>{name1}</a>
          {name2 ? (
            <>
              {' & '}
              <a href={getProfileUrlById(secondId)}>{name2}</a>
            </>
          ) : null}
        </div>
        {team ? (
          <div className="leaderboard_team">{team}</div>
        ) : null}
      </div>
    );
  };

  return {
    Place: <span className="leaderboard__place">{get(entry, 'TieRank')}</span>,
    Name: buildName(
      get(entry, 'FontName', ''),
      get(entry, 'FishermenID', 0),
      get(entry, 'PartnerFontName', ''),
      get(entry, 'PartnerFishermenId', 0),
      get(entry, 'teamname', ''),
    ),
    'Total<br/>Fish / Weight': (
      <span>
        <span className="count">{entry.accum_SumFishCount}</span> /{' '}
        <span className="weight">
          {prettifyOunces(get(entry, 'overall_SumTotalOuncesAfterPenalty'), false)}
        </span>
      </span>
    ),
    ...allDays,
  };
}
